import { CLINICAL_ARTICLES } from "./articles-clinical";

const img = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1400&q=80`;

const CORE_ARTICLES = [
  {
    slug: "low-testosterone-symptoms",
    image: img("photo-1782157909774-c7bb05c035dc"),
    imageAlt: "Man with a beard looking thoughtfully at the camera",
    featured: true,
    label: "SYMPTOMS & DIAGNOSIS",
    title: "Low testosterone symptoms in men",
    lede: "The symptoms attributed to low testosterone are common — which is exactly why they prove so little on their own. Here is what the pattern actually looks like, and how diagnosis works.",
    reading: "6 min read",
    updated: "February 2026",
    author: "Androgen Health clinical team",
    answer:
      "Common symptoms of low testosterone include persistent fatigue, reduced libido, low mood and irritability, reduced muscle strength, increased body fat, poor concentration and disturbed sleep. But all of these have other common causes — which is why diagnosis requires appropriate blood testing and clinical assessment, never symptoms alone.",
    body: [
      {
        h2: "The symptoms, honestly described",
        paras: [
          "The textbook list is long: tiredness that rest doesn't fix, fading interest in sex, low mood, brain fog, loss of morning erections, reduced strength, more fat around the middle, and sleep that doesn't restore. In fuller deficiency, some men notice reduced body hair growth and fewer spontaneous erections.",
          "What the list rarely says is that every item on it is also caused by ordinary life: stress, poor sleep, excess weight, low activity, low mood itself, and dozens of medical conditions and medications. That is the trap the honest clinics warn about — a symptom checklist proves nothing on its own.",
        ],
      },
      {
        h2: "Why the pattern matters more than any single symptom",
        paras: [
          "Testosterone deficiency is suspected when several symptoms persist together — particularly the combination of low libido and fatigue with other changes — and especially when they have changed alongside general health. A clinician is looking for a coherent picture with timing and context, not a bingo card.",
        ],
      },
      {
        h2: "Where blood testing fits",
        paras: [
          "Testing turns a suspicion into something that can be examined: a morning total testosterone measurement, with free testosterone and SHBG where indicated, and often a repeat to confirm. Where to draw the line between 'borderline' and 'deficient' is a clinical judgement made with the assay's reference range, your symptoms and your history — which is why a number from a kit, by itself, is not a diagnosis.",
        ],
        bullets: [
          "Morning collection, because testosterone is highest and most reliable early in the day.",
          "Repeat testing for borderline results before any diagnosis is made.",
          "Further hormones (LH, FSH, prolactin) to look for the cause, not just the fact.",
        ],
      },
      {
        h2: "When to seek assessment — and when to seek it urgently",
        paras: [
          "Seek a routine assessment if several symptoms have persisted for months and are affecting your life. Seek input sooner if symptoms are severe, worsening quickly, or accompanied by other changes — and urgently (999 or 111 in the UK) for anything suggesting a serious acute problem: chest pain, one-sided weakness, slurred speech, or thoughts of self-harm.",
        ],
      },
    ],
    faqs: [
      {
        q: "What happens if testosterone is low and untreated?",
        a: "Untreated deficiency can affect quality of life, sexual function, body composition and bone health over time. Whether treatment is appropriate still depends on the confirmed cause and your individual risks — which is what assessment establishes.",
      },
      {
        q: "Can low testosterone be caused by something other than age?",
        a: "Yes. Causes include problems in the testes or the pituitary signals that drive them, obesity, sleep disorders, some medications and other medical conditions. Finding the cause is part of proper diagnosis, because it can change the right treatment entirely.",
      },
    ],
    related: ["normal-testosterone-levels", "causes-of-low-testosterone"],
  },

  {
    slug: "normal-testosterone-levels",
    image: img("photo-1739285388427-d6f85d12a8fc"),
    imageAlt: "Two men sitting at a desk talking",
    label: "TESTING EXPLAINED",
    title: "What is a normal testosterone level?",
    lede: "Reference ranges, nmol/L, and why a number that is 'normal' for the laboratory may not be the whole answer for you.",
    reading: "5 min read",
    updated: "February 2026",
    author: "Androgen Health clinical team",
    answer:
      "UK laboratories commonly quote a reference interval of roughly 8–30 nmol/L for adult male total testosterone, measured in the morning. But 'normal' depends on the assay used, the timing of the sample and your symptoms: a result within the range can still sit too low for a particular man, and a low-looking result without symptoms may need no treatment at all.",
    body: [
      {
        h2: "The reference range, demystified",
        paras: [
          "A reference range is the band into which most results from healthy men fall for that specific laboratory assay — not a line drawn in nature. Most UK assays quote something close to 8–30 nmol/L for adult men, sampled in the morning. Values are reported in nanomoles per litre (nmol/L) in the UK, while US results use ng/dL — a common source of confusion when comparing results online.",
        ],
      },
      {
        h2: "Levels by age — context, not targets",
        paras: [
          "Testosterone declines gradually with age, on average. That has two implications worth holding together: a mildly lower level in a man in his sixties may be unremarkable, and 'levels by age' charts are population averages — not prescriptive targets. The clinical question is whether your level, in your body, explains your symptoms.",
        ],
      },
      {
        h2: "Total vs free testosterone",
        paras: [
          "Most testosterone in the blood is bound to proteins, chiefly SHBG; only the small unbound fraction is biologically active. That is why two men with the same total testosterone can have very different available levels — and why SHBG and free testosterone are measured when the total alone doesn't tell the story.",
        ],
      },
      {
        h2: "The limits of a single number",
        paras: [
          "Testosterone varies day to day and even hour to hour. It is suppressed by illness, stress and poor sleep, and samples taken in the afternoon read lower. This is why clinicians collect in the morning, repeat borderline results, and interpret the number with you — not instead of you.",
        ],
      },
    ],
    faqs: [
      {
        q: "My result is 9 nmol/L — do I need treatment?",
        a: "Not necessarily. A result near the lower end of the range needs interpreting alongside symptoms, timing and confirmation. Some men at that level need treatment; many need a repeat test, a cause investigated, or nothing at all.",
      },
      {
        q: "Are US and UK testosterone units the same?",
        a: "No. UK results are in nmol/L; US results are usually in ng/dL. To convert, divide ng/dL by roughly 28.4 — a '500' from an American lab is about 17.6 nmol/L.",
      },
    ],
    related: ["low-testosterone-symptoms", "causes-of-low-testosterone"],
  },

  {
    slug: "causes-of-low-testosterone",
    image: img("flagged/photo-1555697752-da25a4b1025b"),
    imageAlt: "Man lying awake in bed in morning light",
    label: "CAUSES & RELATED HEALTH",
    title: "Causes of low testosterone",
    lede: "From the testes to the pituitary to the metabolic — where testosterone problems actually come from, and why the cause changes the plan.",
    reading: "6 min read",
    updated: "February 2026",
    author: "Androgen Health clinical team",
    answer:
      "Low testosterone has causes at three levels: the testes themselves (primary hypogonadism), the pituitary signals that drive them (secondary hypogonadism), and broader factors that suppress both — obesity, poor sleep, illness and some medications. Finding the cause matters because it can change the right treatment: some causes are treatable directly, and TRT is not always the answer.",
    body: [
      {
        h2: "Primary vs secondary hypogonadism",
        paras: [
          "Primary means the problem is in the testes — they are not producing enough despite normal signals from the brain. Secondary means the signal itself is weak: the pituitary is not sending enough LH and FSH. The distinction is made with LH and FSH results and matters enormously: secondary causes in particular can be reversible or need a different therapeutic approach entirely.",
        ],
      },
      {
        h2: "The causes that live in ordinary life",
        paras: [
          "Obesity is one of the most common contributors: excess fat tissue converts testosterone to oestrogen and dampens the brain's signal, which is why weight loss can raise testosterone meaningfully. Poor or short sleep lowers it. Opioid medications, some antidepressants and steroids suppress it. Chronic illness does too. These causes are common, real — and often partially reversible without a prescription.",
        ],
        bullets: [
          "Obesity and metabolic health — a major, modifiable driver.",
          "Sleep quality and duration — testosterone is produced largely during sleep.",
          "Medications — including opioids and long-term steroid use.",
          "Acute and chronic illness — which suppress levels temporarily or persistently.",
        ],
      },
      {
        h2: "Why the cause changes the treatment",
        paras: [
          "If excess weight and poor sleep are suppressing your levels, the evidence-led first move is treating those — and reassessing. If the cause is a pituitary problem, a different approach may be indicated, sometimes including fertility-preserving treatments that standard TRT would undermine. This is the difference between a clinic that sells treatment and a clinic that practices medicine.",
        ],
      },
      {
        h2: "Testosterone, fertility and metabolic health",
        paras: [
          "The threads connect: metabolic health influences testosterone, testosterone influences body composition, and treatment decisions influence fertility. Any of them can change the plan. It is why assessment investigates causes rather than reacting to a single low result — and why fertility counselling comes before any prescription.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can losing weight raise my testosterone?",
        a: "Often, yes. Weight loss — particularly around the middle — is one of the best-evidenced ways to raise testosterone in men with obesity, alongside treating poor sleep. It may be the treatment, not just an adjunct.",
      },
      {
        q: "What is the difference between primary and secondary hypogonadism?",
        a: "Primary means the testes are not producing enough; secondary means the pituitary signal driving them is too weak. They are distinguished with LH and FSH testing and can lead to different treatments.",
      },
    ],
    related: ["low-testosterone-symptoms", "normal-testosterone-levels"],
  },

  {
    slug: "testosterone-gel-vs-injections",
    image: img("photo-1666887360680-9dc27a1d2753"),
    imageAlt: "Clinician checking a patient during a consultation",
    featured: true,
    label: "TREATMENT COMPARISON",
    title: "Testosterone gel vs injections",
    lede: "The two main UK options, compared honestly: routine, stability, side effects and what actually drives the choice.",
    reading: "5 min read",
    updated: "February 2026",
    author: "Androgen Health clinical team",
    answer:
      "Gels provide steady levels with a simple daily routine but require consistent application and care to avoid skin-to-skin transfer. Injections require less frequent dosing and more predictable absorption, but levels can swing between doses and some men feel those peaks and troughs. Neither is universally better — the right choice depends on your SHBG, routine, preferences and how your body responds, decided with your clinician.",
    body: [
      {
        h2: "How gels work",
        paras: [
          "A transdermal gel is applied daily to clean, dry skin — usually shoulder or upper arm — and absorbs steadily through the day. Levels tend to be even, and the option is easy to stop or adjust, which suits some men. The trade-offs: it is a daily commitment, application sites need care, and the gel must dry fully before skin-to-skin contact with partners or children. Absorption also varies between men, which is why levels are checked after starting.",
        ],
      },
      {
        h2: "How injections work",
        paras: [
          "Injectable testosterone — from shorter-acting preparations given weekly or fortnightly to longer-acting depots given every several weeks — bypasses the skin entirely. Absorption is predictable and the routine is sparse. The trade-offs: levels rise and fall between doses, some men notice that fluctuation as peaks and troughs in energy or mood, and injections involve a needle and a technique you will be supported to learn.",
        ],
      },
      {
        h2: "What actually drives the choice",
        bullets: [
          "SHBG and free testosterone — your binding profile shapes how you handle each route.",
          "Routine and preference — daily ritual versus less frequent dosing.",
          "How you respond — response is assessed in review, and switching is a recognised clinical decision.",
          "Cost differences between formulations — made clear before you commit.",
        ],
      },
      {
        h2: "The comparison, in one table",
        table: [
          ["", "Gel", "Injection"],
          ["Routine", "Daily application", "Weekly to multi-weekly"],
          ["Level stability", "Steady", "Peaks and troughs between doses"],
          ["Key caution", "Transfer to others; variable absorption", "Needle technique; level fluctuation"],
          ["Stopping or adjusting", "Easy and rapid", "Depends on the preparation"],
        ],
      },
      {
        h2: "How long any of it takes to work",
        paras: [
          "Some effects — libido most often — are noticed within weeks; changes in energy, body composition and strength typically unfold over months. Structured reviews assess your response rather than promising a timeline, and monitoring continues on a schedule regardless of how well you feel.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I switch from gel to injections later?",
        a: "Yes. Switching is a recognised clinical decision, made on response, tolerability and preference with your clinician — with monitoring adjusted around the change.",
      },
      {
        q: "Do injections hurt?",
        a: "Most men find intramuscular or subcutaneous injections manageable with proper technique and support. Technique training is part of starting treatment, and many men move to self-administration confidently.",
      },
    ],
    related: ["trt-treatment-options", "trt-monitoring", "normal-testosterone-levels"],
  },
];

export const ARTICLES = [...CORE_ARTICLES, ...CLINICAL_ARTICLES];
export const FEATURED_ARTICLES = ARTICLES.filter((a) => a.featured);
